import {
  Button,
  Input,
  Listbox,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@heroui/react"
import { Trans, useLingui } from "@lingui/react/macro"
import type { CollectionElement } from "@react-types/shared/src/collections"
import { useMemo, useState } from "react"

export interface FilterModalProps<T> {
  items: T[]
  filter: (value: T, query: string) => boolean
  includeValue: Set<string>
  excludeValue: Set<string>
  includeLabel: string
  excludeLabel: string
  onIncludeChange: (value: Set<string>) => void
  onExcludeChange: (value: Set<string>) => void
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  header: React.ReactNode
  children: (item: T) => CollectionElement<T>
  footerActions?: React.ReactNode
}

export function FilterModal<T extends object>({
  items,
  filter,
  includeValue,
  excludeValue,
  includeLabel,
  excludeLabel,
  onIncludeChange,
  onExcludeChange,
  isOpen,
  onOpenChange,
  header,
  children,
  footerActions,
}: FilterModalProps<T>) {
  const { t } = useLingui()
  const [leftSearch, setLeftSearch] = useState("")
  const [rightSearch, setRightSearch] = useState("")

  const leftOptions = useMemo(
    () => items.filter(item => filter(item, leftSearch)),
    [items, leftSearch, filter]
  )
  const rightOptions = useMemo(
    () => items.filter(item => filter(item, rightSearch)),
    [items, rightSearch, filter]
  )

  const handleInclude = (newInclude: Set<string>) => {
    // Remove from exclude if present
    const newExclude = excludeValue.difference(newInclude)
    if (newExclude.size !== excludeValue.size) {
      onExcludeChange(newExclude)
    }
    // Add to include
    onIncludeChange(newInclude)
  }

  const handleRemove = (newExclude: Set<string>) => {
    // Remove from exclude if present
    const newInclude = includeValue.difference(newExclude)
    if (newInclude.size !== includeValue.size) {
      onIncludeChange(newInclude)
    }
    // Add to exclude
    onExcludeChange(newExclude)
  }

  return (
    <Modal isOpen={isOpen} size="5xl" onOpenChange={onOpenChange}>
      <ModalContent>
        <ModalHeader css="flex items-center gap-2">{header}</ModalHeader>
        <ModalBody>
          <div className="flex gap-4">
            <div className="flex-1">
              <Input
                classNames={{ inputWrapper: "border-1 border-success/50" }}
                color="success"
                css="mb-2 outline-1"
                label={includeLabel}
                placeholder="Search…"
                value={leftSearch}
                variant="bordered"
                onValueChange={setLeftSearch}
              />
              <Listbox<T>
                css="max-h-96 min-h-80 overflow-auto"
                disallowEmptySelection={false}
                items={leftOptions}
                selectedKeys={includeValue}
                selectionMode="multiple"
                variant="flat"
                onSelectionChange={list => handleInclude(list as Set<string>)}
              >
                {children}
              </Listbox>
            </div>

            <div
              className="flex-1"
              style={includeValue.size > 0 ? { opacity: 0.4 } : undefined}
            >
              <Input
                classNames={{ inputWrapper: "border-1 border-danger/50" }}
                color="danger"
                css="mb-2"
                label={excludeLabel}
                placeholder={t`Search…`}
                value={rightSearch}
                variant="bordered"
                onValueChange={setRightSearch}
              />
              <Listbox
                className="max-h-96 min-h-80 overflow-auto"
                disallowEmptySelection={false}
                items={rightOptions}
                selectedKeys={excludeValue}
                selectionMode="multiple"
                variant="flat"
                onSelectionChange={list => handleRemove(list as Set<string>)}
              >
                {children}
              </Listbox>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          {footerActions != null && (
            <div css="flex items-center justify-end gap-2">{footerActions}</div>
          )}
          <div css="flex grow items-center justify-end gap-2">
            <Button
              onPress={() => {
                onIncludeChange(new Set())
                onExcludeChange(new Set())
                setLeftSearch("")
                setRightSearch("")
              }}
            >
              <Trans>Clear</Trans>
            </Button>
            <Button color="primary" onPress={() => onOpenChange(false)}>
              <Trans>Done</Trans>
            </Button>
          </div>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
